import { HeaderText } from '@make-software/cspr-design';
import * as Sentry from '@sentry/react';
import { Component, type ReactNode } from 'react';

import { RootView } from './styles';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * A stale deployment makes lazy route chunks 404 (their content hashes no
 * longer exist on the server). Tag these failures, but never automatically
 * reload: signing callbacks and submitted transaction hashes can be in memory.
 */
const isStaleChunkError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error);

  return /dynamically imported module|Loading chunk|Importing a module script failed|error loading dynamically/i.test(
    message
  );
};

export class AppErrorBoundary extends Component<Props> {
  render() {
    return (
      <Sentry.ErrorBoundary
        fallback={({ error }) => {
          return (
            <RootView>
              <HeaderText size={2}>
                {isStaleChunkError(error)
                  ? 'This page could not load after an update. If you were signing or sending a transaction, check its status in your wallet before refreshing.'
                  : "It looks like we're having issues. Our team was notified, please refresh your browser to try again."}
              </HeaderText>
            </RootView>
          );
        }}
        beforeCapture={(scope, error) => {
          scope.setTag('errorBoundary', 'AppErrorBoundary');
          scope.setTag('staleChunk', isStaleChunkError(error) ? 'yes' : 'no');
        }}
      >
        {this.props.children}
      </Sentry.ErrorBoundary>
    );
  }
}
